// Os meios de pagamento que dependem desta credencial, com o que dá para fazer com cada um.
//
// A lista existe para dois trabalhos opostos: entender o alcance antes de mexer na credencial, e
// esvaziá-la — movendo os vínculos para outra — quando ela é uma cópia duplicada da mesma conta.
import {
  ExternalLinkIcon,
  MoreHorizontalIcon,
  MoveRightIcon,
  PowerOffIcon,
  Trash2Icon,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { FRONTEND_ROUTES } from "@/constants"
import type { MerchantProcessorLinkedConfig } from "@/types/admin-merchant-processor"

const prefix = "pages.adminMerchantProcessors"

type ProcessorLinkedConfigsProps = {
  configs: MerchantProcessorLinkedConfig[]
  /** Nome do white label pelo id; sem ele sobra o uuid, que não diz nada. */
  whiteLabelName: (id: string) => string
  onMove: (config: MerchantProcessorLinkedConfig) => void
  onToggleActive: (
    config: MerchantProcessorLinkedConfig
  ) => void
  onDelete: (config: MerchantProcessorLinkedConfig) => void
}

export const ProcessorLinkedConfigs = ({
  configs,
  whiteLabelName,
  onMove,
  onToggleActive,
  onDelete,
}: ProcessorLinkedConfigsProps) => {
  const { t } = useTranslation()

  if (configs.length === 0) {
    return (
      <p className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
        {t(`${prefix}.detail.noLinkedConfigs`)}
      </p>
    )
  }

  return (
    <ul className="space-y-2">
      {configs.map((config) => (
        <li
          key={config.configId}
          data-testid={`linked-config-${config.configId}`}
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card p-3"
        >
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">
              {t(
                `pages.adminProcessorConfig.methods.${config.method}`,
                { defaultValue: config.method ?? "" }
              )}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {whiteLabelName(config.whiteLabelId ?? "")}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {config.fallbackOnly ? (
              <Badge
                variant="outline"
                className="border-violet-200 bg-violet-50 font-normal text-violet-700"
              >
                {t(`${prefix}.detail.fallbackOnly`)}
              </Badge>
            ) : null}
            <Badge
              variant="outline"
              className={
                config.active
                  ? "border-emerald-200 bg-emerald-50 font-normal text-emerald-700"
                  : "border-gray-200 bg-gray-50 font-normal text-gray-700"
              }
            >
              {config.active
                ? t(`${prefix}.detail.active`)
                : t(`${prefix}.detail.inactive`)}
            </Badge>

            {/* Abrir é a ação mais frequente, então fica fora do menu. */}
            <Button asChild variant="outline" size="sm">
              <Link
                to={FRONTEND_ROUTES.ADMIN_PAYMENT_METHOD_DETAIL.href.replace(
                  ":configId",
                  config.configId
                )}
              >
                <ExternalLinkIcon
                  className="mr-1.5 size-3.5"
                  aria-hidden="true"
                />
                {t(`${prefix}.detail.open`)}
              </Link>
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t(
                    `${prefix}.detail.moreActions`,
                    {
                      method: config.method ?? "",
                    }
                  )}
                >
                  <MoreHorizontalIcon className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => onMove(config)}
                >
                  <MoveRightIcon
                    className="mr-2 size-4"
                    aria-hidden="true"
                  />
                  {t(`${prefix}.detail.move`)}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onToggleActive(config)}
                >
                  <PowerOffIcon
                    className="mr-2 size-4"
                    aria-hidden="true"
                  />
                  {config.active
                    ? t(`${prefix}.detail.deactivate`)
                    : t(`${prefix}.detail.activate`)}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => onDelete(config)}
                >
                  <Trash2Icon
                    className="mr-2 size-4"
                    aria-hidden="true"
                  />
                  {t(`${prefix}.detail.delete`)}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </li>
      ))}
    </ul>
  )
}
